/**
 * useCamera — camera/device selection and permission state
 * Phase 4 (PRI-291): extracted from useStreamSession
 *
 * Owns: cameraPermission, devices, selectedDeviceId, isFrontCamera, localStream, frozenFrameDataUrl
 * Setters returned to orchestrator; the actual getUserMedia calls stay in the orchestrator
 * since they're interleaved with the streaming lifecycle.
 */

import { useState } from 'react'

export interface CameraReturn {
  cameraPermission: 'unknown' | 'granted' | 'denied'
  setCameraPermission: React.Dispatch<React.SetStateAction<'unknown' | 'granted' | 'denied'>>
  devices: MediaDeviceInfo[]
  setDevices: React.Dispatch<React.SetStateAction<MediaDeviceInfo[]>>
  selectedDeviceId: string | null
  setSelectedDeviceId: React.Dispatch<React.SetStateAction<string | null>>
  isFrontCamera: boolean
  setIsFrontCamera: React.Dispatch<React.SetStateAction<boolean>>
  localStream: MediaStream | null
  setLocalStream: React.Dispatch<React.SetStateAction<MediaStream | null>>
  frozenFrameDataUrl: string | null
  setFrozenFrameDataUrl: React.Dispatch<React.SetStateAction<string | null>>
  resetCamera: () => void
}

export function useCamera(): CameraReturn {
  const [cameraPermission, setCameraPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown')
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [isFrontCamera, setIsFrontCamera] = useState<boolean>(false)
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [frozenFrameDataUrl, setFrozenFrameDataUrl] = useState<string | null>(null)

  const resetCamera = () => {
    // localStream tracks are stopped by the orchestrator before calling resetCamera
    setLocalStream(null)
    setFrozenFrameDataUrl(null)
    // Keep permission/devices/selectedDeviceId — user hasn't changed their setup
  }

  return {
    cameraPermission,
    setCameraPermission,
    devices,
    setDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    isFrontCamera,
    setIsFrontCamera,
    localStream,
    setLocalStream,
    frozenFrameDataUrl,
    setFrozenFrameDataUrl,
    resetCamera,
  }
}
